/* eslint-disable @typescript-eslint/no-explicit-any */
import './DefaultCalendar.css';

import { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import { BluContainerBlock } from '@bludata/components';
import {
  DefaultCalendarResetSelected,
  DefaultCalendarSelectPointOfSale,
  DefaultCalendarSetSelectedDate,
} from './store';
import dayjs from 'dayjs';
import { isSafari } from 'react-device-detect';
import { AppDispatch, RootState } from 'packages/blu-booking/src/store';
import { setConfig } from '../../utility_objects/WhiteCache';
import { setAppointments, setRefetch } from '../../store/Appointment';
import { fetchAppointments } from 'packages/blu-booking/src/data_fetching/Appointment/Appointments';
import { AppointmentSearch } from '../../UI/AppointmentSearch/AppointmentSearch';
import { CalendarHeader } from '../../UI/CalendarHeader/CalendarHeader';
import { Header } from './UI/Header';
import { Filters } from './UI/Filters';
import { Scheduler } from './UI/Scheduler';
import { Default } from 'react-if';
import { CalendarDefaultInner } from './DefaultCalendarInner';
import { FasciaPresenze } from '../../UI/Presenze/FasciaPresenze';
import { getParametriAppuntamentiDefault } from './utility_objects/AppuntamentiDefault';

export const DefaultCalendar = memo(function Calendar(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const { refetch } = useSelector(
    (state: RootState) => state.bluBookingaAppointments
  );
  const { filtersLoad, pointOfSales } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );
  const {
    selectedDate,
    view,
    selectedServices: checkedServices,
    selectedPointOfSale,
    selectedUsers: checkedUsers,
    selectedStudi: checkedStudi,
    client,
    presenzeModo,
  } = useSelector((state: RootState) => state.bluBookingDefaultCalendar);

  const uderlineNotification = (event: any) => {
    if (event && event.detail) {
      setConfig('underline', event.detail?.id);

      const isSamePointOfSale =
        selectedPointOfSale?.id === event.detail?.pointOfSaleId;
      const date = dayjs(selectedDate).format('YYYY/MM/DD');
      const appointmentDate = dayjs(event.detail.startTime).format(
        'YYYY/MM/DD'
      );

      if (!isSamePointOfSale && event.detail?.pointOfSaleId in pointOfSales)
        dispatch(
          DefaultCalendarSelectPointOfSale(
            pointOfSales[event.detail?.pointOfSaleId]
          )
        );

      if (checkedStudi.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedStudi'));

      if (checkedServices.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedServices'));

      if (checkedUsers.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedUsers'));

      if (date === appointmentDate) {
        dispatch(setRefetch());
        return;
      }
      dispatch(DefaultCalendarSetSelectedDate(event.detail.startTime));
    }
    return;
  };

  useEffect(() => {
    window.addEventListener('BluBookingNotification', uderlineNotification);

    return () => {
      window.removeEventListener(
        'BluBookingNotification',
        uderlineNotification
      );
    };
  });

  useEffect(() => {
    if (!filtersLoad) return;
    const timeoutId = setTimeout(() => {
      // Stessi parametri della chiave con cui la pagina riconosce la lista
      dispatch(
        fetchAppointments(
          getParametriAppuntamentiDefault({
            selectedDate,
            view,
            selectedServices: checkedServices,
            selectedStudi: checkedStudi,
            selectedUsers: checkedUsers,
            selectedPointOfSale,
            email: client?.email,
          })
        )
      );
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedDate, checkedServices, checkedUsers, selectedPointOfSale, checkedStudi, dispatch, view, refetch, client?.email, filtersLoad]);

  const handleSearch = useCallback(
    (appointment: any) => {
      const d = dayjs(selectedDate).format('DD/MM/YYYY');
      if (d !== appointment?.date)
        dispatch(
          DefaultCalendarSetSelectedDate(
            new Date(dayjs(appointment?.date, 'DD/MM/YYYY').format())
          )
        );

      if (
        appointment?.pointOfSaleId &&
        pointOfSales &&
        appointment?.pointOfSaleId !== selectedPointOfSale?.id &&
        appointment?.pointOfSaleId in pointOfSales
      )
        dispatch(
          DefaultCalendarSelectPointOfSale(
            pointOfSales[appointment?.pointOfSaleId]
          )
        );
      if (checkedStudi.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedStudi'));

      if (checkedServices.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedServices'));

      if (checkedUsers.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedUsers'));

      if (d === appointment?.date) dispatch(setRefetch());
    },
    [
      checkedServices,
      checkedStudi,
      checkedUsers,
      dispatch,
      pointOfSales,
      selectedDate,
      selectedPointOfSale?.id,
    ]
  );

  return (
    <>
      <Stack
        key="DefaultCalendar "
        className={isSafari ? 'safari-scroll' : ''}
        flex={1}
        flexDirection="column"
      >
        <AppointmentSearch action={handleSearch} />
        <CalendarHeader title="Agenda / Calendario appuntamenti" id="default" />

        <BluContainerBlock
          key="DefaultCalendar1"
          sx={{
            m: '4px',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {!filtersLoad ||
          (filtersLoad &&
            pointOfSales &&
            Object.keys(pointOfSales).length > 0) ? (
            <>
              <Header />
              {/* Fascia Presenze sopra la riga dei giorni, fuori da Syncfusion: nel
                  Mese non c'è. */}
              {presenzeModo !== 'nessuna' &&
                selectedPointOfSale &&
                view !== 'Month' && <FasciaPresenze />}
              <Box
                id="calendar-container"
                sx={{
                  backgroundColor: 'var(--base-foreground-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                <CalendarDefaultInner />
              </Box>
            </>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              direction="row"
              flex="1"
              sx={{ height: '100%', width: '100%' }}
            >
              <Typography fontWeight="bold">
                Nessun punto vendita visibile dall'utente, controllare
                configurazione.
              </Typography>
            </Stack>
          )}
        </BluContainerBlock>
      </Stack>
      <Filters />
    </>
  );
});
